import React, { useState } from 'react';
import {
  FiPlus,
  FiMessageSquare,
  FiSearch,
  FiX,
  FiArrowLeft,
  FiBriefcase,
  FiArchive,
} from 'react-icons/fi';
import { useMuteConversation, type ConversationItem } from '../../api/conversations';
import { useAuth } from '../../hooks/useAuth';
import { ChatAvatar, pickDisplayName } from './ChatAvatar';
import { useGetUserProfile } from '../../api/profile';
import { toast } from '../../utils/toast';

interface ConversationListProps {
  conversations: ConversationItem[];
  selectedUuid?: string;
  onSelect: (conversation: ConversationItem) => void;
  onNewChat?: () => void;
  isLoading?: boolean;
  isCollapsed?: boolean;
}

function formatSnippetTime(isoString?: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = new Date(now.getTime() - 86400000).toDateString() === date.toDateString();

  if (isToday) {
    return date
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();
  }
  if (isYesterday) return 'Yesterday';

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Extracted into its own component so it can call useGetUserProfile (hooks can't be called in a loop)
const ConversationListItem: React.FC<{
  conv: ConversationItem;
  isSelected: boolean;
  currentUserId: number;
  onSelect: (conversation: ConversationItem) => void;
  isCollapsed?: boolean;
}> = ({ conv, isSelected, currentUserId, onSelect, isCollapsed = false }) => {
  const other =
    conv.other_participant ||
    conv.participants?.find((p) => p.id !== currentUserId) ||
    conv.participants?.[0];

  const otherUserId = other?.id;

  // Fetch full profile to get the profile picture
  const { data: otherProfile } = useGetUserProfile(otherUserId, Boolean(otherUserId));

  const displayName =
    pickDisplayName(other?.full_name, other?.username, conv.title, conv.name) || 'Contact';

  // Prefer fetched profile picture, fall back to conversation participant data
  const avatarUrl =
    otherProfile?.prof_pics ||
    otherProfile?.prof_pics_url ||
    otherProfile?.google_prof_pics ||
    other?.profile_picture ||
    other?.prof_pics ||
    other?.google_prof_pics;

  const lastMessageText =
    conv.last_message?.body || conv.last_message_text || 'Started a conversation';

  const messageTime = formatSnippetTime(
    conv.last_message?.created_at || conv.last_message_time || conv.updated_at,
  );

  const unreadCount = conv.unread_count ?? 0;
  const hasUnread = unreadCount > 0;
  const contextTitle = conv.context?.title;

  const muteMutation = useMuteConversation();

  const handleToggleMute = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const conversationUuid = conv.conversation_uuid || conv.id;
    if (!conversationUuid) return;
    try {
      const targetMuteState = !conv.is_muted;
      await muteMutation.mutateAsync({
        conversationUuid,
        isMuted: targetMuteState,
      });
      toast.success(targetMuteState ? 'Conversation archived' : 'Conversation unarchived');
    } catch (err) {
      console.error('Failed to archive/unarchive conversation:', err);
      toast.error('Failed to update archive settings.');
    }
  };

  if (isCollapsed) {
    return (
      <button
        onClick={() => onSelect(conv)}
        className={`relative flex items-center justify-center h-12 w-12 rounded-xl transition-all duration-200 cursor-pointer group mx-auto ${
          isSelected ? 'bg-[#1784FF] text-white shadow-lg' : 'bg-transparent hover:bg-[#1C1C1E]'
        }`}
        title={displayName}
      >
        {/* Selected Accent Line */}
        {isSelected && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-r-full bg-white" />
        )}

        <ChatAvatar src={avatarUrl} name={displayName} size="sm" />

        {hasUnread && !isSelected && (
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5 rounded-full bg-[#1784FF] border border-[#161618]" />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={() => onSelect(conv)}
      className={`relative flex w-full items-center gap-3.5 p-3 rounded-2xl text-left transition-all duration-200 cursor-pointer overflow-hidden group ${
        isSelected
          ? 'bg-[#1784FF] shadow-[0_4px_16px_rgba(23,132,255,0.2)]'
          : 'bg-transparent hover:bg-[#1C1C1E]'
      }`}
    >
      {/* Selected Accent Line */}
      {isSelected && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1 rounded-r-full bg-white shadow-sm" />
      )}

      {/* Avatar */}
      <ChatAvatar src={avatarUrl} name={displayName} size="lg" />

      {/* Text Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-center h-full">
        <div className="flex items-center justify-between gap-1 mb-0.5">
          {/* Gig/service is the primary distinguisher between conversations, so it leads */}
          {contextTitle ? (
            <h4
              className={`text-[15px] font-bold truncate flex items-center gap-1 ${isSelected ? 'text-white' : 'text-white'}`}
            >
              <FiBriefcase
                size={12}
                className={`shrink-0 ${isSelected ? 'text-white/80' : 'text-[#1784FF]'}`}
              />
              <span className="truncate">{contextTitle}</span>
            </h4>
          ) : (
            <h4
              className={`text-[15px] font-bold truncate ${isSelected ? 'text-white' : 'text-zinc-100'}`}
            >
              {displayName}
            </h4>
          )}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={handleToggleMute}
              disabled={muteMutation.isPending}
              title={conv.is_muted ? 'Unarchive conversation' : 'Archive conversation'}
              className={`flex h-6 w-6 items-center justify-center rounded-full transition-all cursor-pointer ${
                conv.is_muted
                  ? isSelected
                    ? 'text-white opacity-100'
                    : 'text-amber-500 opacity-100'
                  : `opacity-0 group-hover:opacity-100 ${isSelected ? 'text-white/70 hover:text-white' : 'text-zinc-500 hover:text-zinc-300'}`
              }`}
            >
              <FiArchive size={13} />
            </button>
            {messageTime && (
              <span
                className={`text-[12px] whitespace-nowrap ${isSelected ? 'text-white/80' : 'text-zinc-500'}`}
              >
                {messageTime}
              </span>
            )}
          </div>
        </div>

        {/* Person's name becomes secondary context once the gig is leading */}
        {contextTitle && (
          <span
            className={`text-[12px] truncate block mb-0.5 ${isSelected ? 'text-white/70' : 'text-zinc-400'}`}
          >
            with {displayName}
          </span>
        )}

        <div className="flex items-center justify-between gap-3">
          <p
            className={`text-[14px] truncate leading-snug ${isSelected ? 'text-white/80' : hasUnread ? 'text-white font-medium' : 'text-zinc-500'}`}
          >
            {lastMessageText}
          </p>

          {hasUnread && !isSelected && (
            <span className="flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#1784FF] text-[11px] font-bold text-white shadow-sm shrink-0">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedUuid,
  onSelect,
  onNewChat,
  isLoading,
  isCollapsed = false,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.id ? Number(user.id) : Number(localStorage.getItem('user_id') || 0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchivedFolder, setShowArchivedFolder] = useState(false);

  const filtered = conversations.filter((c) => {
    const other =
      c.other_participant ||
      c.participants?.find((p) => p.id !== currentUserId) ||
      c.participants?.[0];
    const name = other?.full_name || other?.username || '';
    const username = other?.username || '';
    const convTitle = c.title || c.name || '';
    const contextTitle = c.context?.title || '';
    const lastMsg = c.last_message?.body || c.last_message_text || '';
    const query = searchTerm.toLowerCase();
    return (
      name.toLowerCase().includes(query) ||
      username.toLowerCase().includes(query) ||
      convTitle.toLowerCase().includes(query) ||
      contextTitle.toLowerCase().includes(query) ||
      lastMsg.toLowerCase().includes(query)
    );
  });

  const activeConversations = filtered.filter((c) => !c.is_muted);
  const archivedConversations = filtered.filter((c) => c.is_muted);

  const totalUnread = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);

  return (
    <div className="flex h-full flex-col bg-[#161618] overflow-hidden min-h-0 border-r border-[#2C2C2E]">
      {/* ── Top Header ───────────────────────────────────────────────────────── */}
      <div
        className={`flex shrink-0 items-center ${isCollapsed ? 'justify-center py-5' : 'justify-between p-4 px-5'}`}
      >
        {isCollapsed ? (
          <div className="relative">
            <FiMessageSquare size={22} className="text-[#1784FF]" />
            {totalUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white shadow-sm animate-pulse border border-[#161618]">
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h2 className="text-[22px] font-bold text-white tracking-tight">Messages</h2>
            {totalUnread > 0 && (
              <span className="flex items-center justify-center h-5 px-1.5 min-w-[20px] rounded-full bg-[#1784FF] text-[11px] font-bold text-white shadow-sm">
                {totalUnread}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Search Input (hidden in collapsed mode) ─────────────────────────── */}
      {!isCollapsed && (
        <div className="px-3 pb-3">
          <div className="relative flex items-center transition-all group">
            <FiSearch
              size={16}
              className="absolute left-3.5 text-zinc-400 group-focus-within:text-[#1784FF] transition-colors"
            />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 rounded-xl bg-[#1C1C1E] py-2 pl-10 pr-10 text-[15px] text-zinc-100 placeholder:text-zinc-500 outline-none border border-transparent focus:border-[#1784FF]/50 focus:bg-[#1C1C1E] transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 p-1 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              >
                <FiX size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Conversations List / Empty States ─────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto custom-modal-scrollbar min-h-0 relative">
        {isLoading ? (
          <div className="space-y-1 p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 p-3 rounded-2xl mx-2 animate-pulse ${isCollapsed ? 'justify-center mx-auto w-12 h-12 p-0' : ''}`}
              >
                <div className="h-10 w-10 rounded-full bg-white/5 shrink-0" />
                {!isCollapsed && (
                  <div className="flex-1 space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="h-4 w-32 rounded bg-white/10" />
                      <div className="h-3 w-10 rounded bg-white/5" />
                    </div>
                    <div className="h-3 w-48 rounded bg-white/5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : showArchivedFolder ? (
          <div className="animate-in fade-in duration-200">
            {/* Archived Chats Back Navigation Row */}
            <div className={`px-2 pt-1 pb-2 ${isCollapsed ? 'flex justify-center' : ''}`}>
              {isCollapsed ? (
                <button
                  onClick={() => setShowArchivedFolder(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-800 hover:bg-zinc-700 text-[#1784FF] transition-all cursor-pointer"
                  title="Back to active chats"
                >
                  <FiArrowLeft size={16} />
                </button>
              ) : (
                <button
                  onClick={() => setShowArchivedFolder(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-white/5 transition-all text-left cursor-pointer"
                >
                  <FiArrowLeft size={18} className="text-[#1784FF]" />
                  <div>
                    <span className="text-sm font-bold text-white block leading-tight">
                      Archived Chats
                    </span>
                    <span className="text-[11px] text-zinc-500">Back to active chats</span>
                  </div>
                </button>
              )}
            </div>

            {archivedConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center p-8 text-zinc-500">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-b from-[#2A2A2D] to-[#1C1C1E] border border-[#3A3A3C] shadow-lg text-zinc-400">
                  <FiArchive size={18} />
                </div>
                {!isCollapsed && (
                  <>
                    <h4 className="text-[15px] font-bold text-white mb-1">No archived chats</h4>
                    <p className="text-[12px] text-zinc-500 max-w-[180px] leading-relaxed">
                      Chats you archive will show up in this folder.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="px-2 pb-20 space-y-2">
                {archivedConversations.map((conv: ConversationItem) => (
                  <ConversationListItem
                    key={conv.conversation_uuid}
                    conv={conv}
                    isSelected={conv.conversation_uuid === selectedUuid}
                    currentUserId={currentUserId}
                    onSelect={onSelect}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="animate-in fade-in duration-200">
            {/* Archived Chats Folder Button at top of list (if archived chats exist) */}
            {archivedConversations.length > 0 && (
              <div className={`px-2 pt-1 pb-2 ${isCollapsed ? 'flex justify-center' : ''}`}>
                {isCollapsed ? (
                  <button
                    onClick={() => setShowArchivedFolder(true)}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/10 text-[#1784FF] transition-all cursor-pointer relative"
                    title={`Archived Chats (${archivedConversations.length})`}
                  >
                    <FiArchive size={16} />
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#1784FF] text-[8px] font-bold text-white border border-[#161618]">
                      {archivedConversations.length}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowArchivedFolder(true)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/10 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-[#1784FF] group-hover:scale-105 transition-transform">
                        <FiArchive size={16} />
                      </div>
                      <div>
                        <span className="text-[14px] font-bold text-zinc-200 block leading-tight">
                          Archived Chats
                        </span>
                        <span className="text-[11px] text-zinc-500">Archived conversations</span>
                      </div>
                    </div>
                    <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-500/20 text-[11px] font-bold text-[#1784FF]">
                      {archivedConversations.length}
                    </span>
                  </button>
                )}
              </div>
            )}

            {activeConversations.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center p-6">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-b from-[#2A2A2D] to-[#1C1C1E] border border-[#3A3A3C] shadow-lg text-zinc-400">
                  <FiMessageSquare size={24} />
                </div>
                {!isCollapsed && (
                  <>
                    <h4 className="text-[17px] font-bold text-white mb-2">No messages found</h4>
                    <p className="text-[14px] text-zinc-500 max-w-[220px] leading-relaxed">
                      {searchTerm
                        ? "We couldn't find any matches for your search."
                        : "You haven't started any conversations yet."}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className={`px-2 pb-20 ${isCollapsed ? 'space-y-3' : 'space-y-0.5'}`}>
                {activeConversations.map((conv: ConversationItem) => (
                  <ConversationListItem
                    key={conv.conversation_uuid}
                    conv={conv}
                    isSelected={conv.conversation_uuid === selectedUuid}
                    currentUserId={currentUserId}
                    onSelect={onSelect}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Floating Action Button for Mobile (hidden if collapsed is true, though collapsed is desktop-only) */}
        {onNewChat && !isCollapsed && (
          <button
            onClick={onNewChat}
            className="absolute bottom-6 right-6 flex md:hidden items-center justify-center h-14 w-14 rounded-full bg-[#1784FF] hover:bg-[#1570d9] text-white shadow-[0_8px_24px_rgba(23,132,255,0.4)] transition-all active:scale-95 z-10"
          >
            <FiPlus size={24} className="stroke-[2.5]" />
          </button>
        )}
      </div>

      {/* Desktop New Chat Button */}
      {onNewChat && (
        <div
          className={`p-4 border-t border-[#2C2C2E] bg-[#161618] ${isCollapsed ? 'flex justify-center' : 'hidden md:block'}`}
        >
          {isCollapsed ? (
            <button
              onClick={onNewChat}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white hover:bg-zinc-200 text-black shadow-sm transition-all cursor-pointer shrink-0"
              title="New Chat"
            >
              <FiPlus size={18} className="stroke-[3]" />
            </button>
          ) : (
            <button
              onClick={onNewChat}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-white hover:bg-zinc-200 text-black py-3 text-[15px] font-bold transition-all shadow-sm"
            >
              <FiPlus size={18} className="stroke-[3]" />
              <span>New Chat</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
